import { useParams } from 'react-router-dom';
import { comics, categories } from '@/mocks/comics';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import SEO from '@/components/base/SEO';
import ComicCard from '@/components/feature/ComicCard';
import { useState } from 'react';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [sortBy, setSortBy] = useState('hot');
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  const category = categories.find((c) => c.slug === slug);
  const categoryComics = comics.filter((c) => c.category === slug);

  const sortedComics = [...categoryComics].sort((a, b) => {
    if (sortBy === 'hot') return b.views - a.views;
    if (sortBy === 'rating') return b.rating - a.rating;
    if (sortBy === 'new') return b.updateTime.localeCompare(a.updateTime);
    return 0;
  });

  const totalPages = Math.ceil(sortedComics.length / pageSize);
  const paginatedComics = sortedComics.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  if (!category) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen pt-24 pb-20 px-4 text-center">
          <h1 className="text-2xl font-heading font-bold text-foreground-950">分类不存在</h1>
        </main>
        <Footer />
      </>
    );
  }

  const pageTitle = `${category.name}韩国漫画 - 韩国免费漫画在线 - 韩国漫画网`;
  const pageDesc = `韩国漫画网为您提供${category.count}部${category.name}类韩国漫画，全部免费在线阅读，下拉式畅快体验，每日更新不断。`;

  const collectionJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${category.name}韩国漫画 - 韩国漫画网`,
    description: pageDesc,
    url: `http://laimim.com/category/${slug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '韩国漫画网',
      url: 'http://laimim.com/',
    },
    about: {
      '@type': 'Thing',
      name: category.name,
    },
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: paginatedComics.map((comic, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        item: {
          '@type': 'CreativeWork',
          name: comic.title,
          author: { '@type': 'Person', name: comic.author },
          description: comic.description,
          url: `http://laimim.com/article/${comic.id}`,
          image: comic.cover,
          aggregateRating: {
            '@type': 'AggregateRating',
            ratingValue: comic.rating,
            bestRating: 10,
            ratingCount: comic.views,
          },
        },
      })),
    },
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(collectionJsonLd) }}
      />
      <SEO
        title={pageTitle}
        description={pageDesc}
        keywords={`${category.name}韩国漫画,${category.name}韩漫,在线看韩漫,韩国漫画网`}
        canonicalUrl={`http://laimim.com/category/${slug}`}
      />
      <Navbar />

      <main className="min-h-screen pt-20">
        <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: category.name },
            ]}
          />

          <div className="py-8">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-14 h-14 md:w-16 md:h-16 flex items-center justify-center rounded-full bg-accent-100 text-accent-500">
                <i className={`${category.icon} text-2xl`} />
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
                  {category.name}
                </h1>
                <p className="text-sm text-foreground-600 mt-1">
                  共 {category.count.toLocaleString()} 部漫画
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3 mb-8 pb-6 border-b border-background-200">
              <span className="text-sm text-foreground-500 whitespace-nowrap">排序：</span>
              {[
                { value: 'hot', label: '最热门' },
                { value: 'rating', label: '最高评分' },
                { value: 'new', label: '最新更新' },
              ].map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => { setSortBy(opt.value); setCurrentPage(1); }}
                  className={`px-4 py-1.5 text-sm rounded-full transition-colors whitespace-nowrap cursor-pointer ${
                    sortBy === opt.value
                      ? 'bg-foreground-950 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-5">
              {paginatedComics.map((comic) => (
                <ComicCard key={comic.id} comic={comic} variant="default" />
              ))}
            </div>

            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-12">
                <button
                  type="button"
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="w-9 h-9 flex items-center justify-center rounded-lg text-sm border border-background-300 text-foreground-600 hover:bg-background-100 disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
                >
                  <i className="ri-arrow-left-s-line" />
                </button>
                {Array.from({ length: totalPages }).map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setCurrentPage(i + 1)}
                    className={`w-9 h-9 flex items-center justify-center rounded-lg text-sm transition-colors cursor-pointer whitespace-nowrap ${
                      currentPage === i + 1
                        ? 'bg-foreground-950 text-background-50'
                        : 'border border-background-300 text-foreground-600 hover:bg-background-100'
                    }`}
                  >
                    {i + 1}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="w-9 h-9 flex items-center justify-center rounded-lg text-sm border border-background-300 text-foreground-600 hover:bg-background-100 disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
                >
                  <i className="ri-arrow-right-s-line" />
                </button>
              </div>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </>
  );
}